import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import { AuthService } from '@sew/ui-components';
import {
    BehaviorSubject,
    delay,
    distinctUntilChanged,
    expand,
    filter,
    firstValueFrom,
    from,
    map,
    Observable,
    ReplaySubject,
    tap,
} from 'rxjs';
import { hasProperty, isArrayOfType, isTypeOfString } from '../../helpers/public-helpers';

export const TOKEN_BASE_HREF = new InjectionToken<string>('OS_API:TOKEN_BASE_HREF');

/**
 * Service for fetching a x-csrf token for the user
 */

@Injectable({
    providedIn: 'root',
})
export class OsApiAuthService {
    private _xCsrfToken = new ReplaySubject<string>(1);
    private authInstanceSubject = new BehaviorSubject<AuthService | null>(null);

    public constructor(
        private http: HttpClient,
        @Inject(TOKEN_BASE_HREF) private tokenURL: string // headerLoginService: HeaderLoginService
    ) {}

    /**
     * Fetch the token from the server on initial app load
     * and later after the refresh runs out
     */
    public fetchXCsrfToken() {
        // @TODO: Error handling
        return this.fetchXCsrfTokenResponse().pipe(
            expand((response) => this.fetchXCsrfTokenResponse().pipe(delay((response.body ?? 1800) * 700))),
            map((response) => response.headers.get('x-csrf-token') as string),
            tap((token) => this.setXCsrfToken(token))
        );
    }

    //TODO: remove this method
    public clearSessionAfterOAuthLogout() {
        return this.http.get<number>('/os/api/oidc/logout', {
            withCredentials: true,
        });
    }

    public async getAccesstokenForAPI(apiName: string): Promise<string | null> {
        const authInstance = await firstValueFrom(
            this.authInstanceSubject.pipe(
                // Warte, bis authInstance gesetzt ist
                filter((auth) => auth !== null)
            )
        );

        try {
            const token = await authInstance!.getTokenForApi(apiName);

            return token;
        } catch (error) {
            console.error(`Failed to fetch access token for API: ${apiName}`, error);

            return null;
        }
    }

    /**
     * Observable for isLoggedIn state from the AuthService instance.
     */
    public async isLoggedIn(): Promise<boolean> {
        const authInstance = await firstValueFrom(
            this.authInstanceSubject.pipe(
                // Warte, bis authInstance gesetzt ist
                filter((auth) => auth !== null)
            )
        );

        return authInstance.isLoggedIn();
    }

    public setAuth(auth: AuthService) {
        this.authInstanceSubject.next(auth);
    }

    /**
     * Gets the permissions of the specified access token name.
     *
     * @param tokenName access token name
     * @returns permissions
     */
    public getPermissions(tokenName: string): Observable<string[]> {
        return from(this.getAccesstokenForAPI(tokenName)).pipe(
            map((token) => {
                if (!token) {
                    console.warn(`Token ${tokenName} not found`);

                    return [];
                }

                if (!token.includes('.') || token.split('.').length !== 3) {
                    console.warn('Token malformed');

                    return [];
                }

                const payloadEncoded = atob(token.split('.')[1]);
                const payload = JSON.parse(payloadEncoded);

                if (!hasProperty<{ permissions: any }, string>(payload, 'permissions')) {
                    console.warn('Permissions not found');

                    return [];
                }

                const permissions = payload.permissions;

                if (!isArrayOfType<string>(permissions, isTypeOfString)) {
                    console.warn('Permissions malformed');

                    return [];
                }

                return permissions;
            })
        );
    }

    private fetchXCsrfTokenResponse() {
        return this.http.get<number>(this.tokenURL, {
            headers: new HttpHeaders({
                'x-csrf-token': 'fetch',
            }),
            withCredentials: true,
            observe: 'response',
        });
    }

    public get xCsrfToken(): Observable<string> {
        return this._xCsrfToken.asObservable().pipe(distinctUntilChanged());
    }

    private setXCsrfToken(newToken: string) {
        this._xCsrfToken.next(newToken);
    }
}
